
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useTransition,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDeepCompareMemo } from "../hooks/useDeepCompare";
import { useViews } from "./ViewsProvider";
import { useFilters } from "./FiltersProvider";
import { EventRowDTO } from "../types/EventRowDTO";
import { BulkSelection } from "../types/BulkSelection";
import { useMetaTable } from "./MetaTableProvider";
import {
  getEvents,
  archiveEvents as archiveEventsApi,
  unarchiveEvents as unarchiveEventsApi,
  deleteEvents as deleteEventsApi,
  updateEvents,
  duplicateEvent as duplicateEventApi,
} from "@/lib/api/handlers/EventHandling";
import {
  throwServerError,
  type ServerError,
  type SaveErrorPayload,
} from "@/lib/api/errors/ErrorHandler";
import { getS3EventImageUrls } from "@/lib/api/handlers/S3Handling";
import type { DuplicateEventResponse, BulkActionResult } from "@/lib/api/clients/EventClient";
import { normalizeBulkOutcome, splitFailedIds } from "@/lib/bulk/bulkFailure";
import { parseEventsSpecial } from "@/lib/utils/parser";
import { eventListProjection } from "@/lib/utils/eventListProjection";
import { isBigForwardJump, toInstantMs } from "@/lib/utils/event-date-bounds";
import { useSignatureEffect } from "../hooks/useSignatureEffect";
import { Field } from "../types/Field";

/** Partial-failure payload returned by bulk event endpoints. `undefined` means the
 *  action was skipped (e.g. empty selection) — treat as no-op.
 *  `failures` carries the per-row reason; read it via `normalizeBulkOutcome`. */
export type EventBulkOutcome = BulkActionResult | undefined;

/** One pending inline edit that would spend an event's one-time jump budget. */
export type PendingDateJump = {
  eventId: string;
  name: string;
  spent: number;
};

/** A pending edit that moves an event's dates — the trigger for the server's
 *  scheduled-automation recompute. */
export type PendingDateEdit = {
  eventId: string;
  name: string;
  plugins: string[];
  oldStart?: string;
  oldEnd?: string;
  newStart?: string;
  newEnd?: string;
  oldTimeZone?: string;
  newTimeZone?: string;
};

interface EventsContextProps {
  duplicateEvent: (
    eventId: string,
    name?: string,
    copyGuests?: boolean,
    // The WHOLE outcome, not just its guest half. `droppedOnClone` rides
    // here too — a cloned formula or calculated automation row the new event
    // cannot hold — and returning only `guestClone` discarded it one layer
    // above the wire that carries it.
  ) => Promise<DuplicateEventResponse>;
  archiveEvents: (selection: BulkSelection) => Promise<EventBulkOutcome>;
  unarchiveEvents: (selection: BulkSelection) => Promise<EventBulkOutcome>;
  deleteEvents: (selection: BulkSelection) => Promise<EventBulkOutcome>;
  buildUpdateEvents: (
    eventId: string,
    field: Field,
    newValue: string | string[],
  ) => void;
  saveUpdateEvents: () => Promise<void>;
  discardPendingUpdates: () => void;
  hasPendingChanges: boolean;
  /** The last failed save, as a backend error code, or null. Without this a
   *  rejected save (e.g. dateJumpLimitReached) only reverted the row on screen
   *  and said nothing about why. */
  saveError: SaveErrorPayload | string | null;
  dismissSaveError: () => void;
  /** The pending edit that would spend the event's one big-date-jump budget,
   *  or null. Lets the caller warn BEFORE the save, the way event settings
   *  does, instead of surfacing the 409 afterwards. */
  getPendingDateJumps: () => PendingDateJump[];
  getPendingDateEdits: () => PendingDateEdit[];
  /** Refuse one event's pending date edit inline, without discarding anything. */
  flagDateJumpBlocked: (eventId: string) => void;
  fieldErrors: Record<string, Record<string, string>>;
  events: EventRowDTO[];
  eventImageUrls: Record<string, string>;
  loading: boolean;
}

const EventsContext = createContext<EventsContextProps | undefined>(undefined);

interface EventsProviderProps {
  children: ReactNode;
  initialEvents: EventRowDTO[];
  eventImageUrls?: Record<string, string>;
}

export const EventsProvider = ({
  children,
  initialEvents,
  eventImageUrls: initialEventImageUrls = {},
}: EventsProviderProps) => {
  const { activeFilterState } = useFilters();
  const { activeViewState } = useViews();
  const {
    archived,
    sortBy,
    sortOrder,
    page,
    pageSize,
    search,
    setTotalCount,
    setPage,
  } = useMetaTable();
  const [isPending, startTransition] = useTransition();
  const [events, setEvents] = useState<EventRowDTO[]>(initialEvents);
  // `events` in closures can go stale when multiple bulk actions run back-to-back; the
  // ref gives `removeItemsAndRefresh` the freshest list for its pagination math.
  const eventsStateRef = useRef<EventRowDTO[]>(initialEvents);
  useEffect(() => { eventsStateRef.current = events; }, [events]);
  const [eventImageUrls, setEventImageUrls] = useState<Record<string, string>>(
    initialEventImageUrls,
  );
  const pendingUpdatesRef = useRef<EventRowDTO[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [saveError, setSaveError] = useState<SaveErrorPayload | string | null>(
    null,
  );
  const dismissSaveError = useCallback(() => setSaveError(null), []);
  const [fieldErrors, setFieldErrors] = useState<Record<string, Record<string, string>>>({});
  const fieldErrorsRef = useRef(fieldErrors);
  fieldErrorsRef.current = fieldErrors;

  const stableFilters = useDeepCompareMemo(activeFilterState.filter);
  const [seedRefreshCounter, setSeedRefreshCounter] = useState(0);

  // Reconcile when the HOST delivers data fresher than the seed we mounted
  // with. Skipping the mount fetch (see the signature effect below) means the
  // seed props are now the ONLY thing on screen until a request parameter
  // changes — so both of them need a listener, exactly like the sibling
  // providers (CheckInLogsProvider / ContactsProvider) already have:
  //
  //  - `initialEvents`: `invalidateEventListCaches` marks the list stale with
  //    `refetchType:"none"`, so a remount inside gcTime paints the PRE-mutation
  //    page synchronously and the background refetch lands on a prop that
  //    `useState` ignores. Without this, an archived or deleted event comes
  //    back on the dashboard.
  //  - `eventImageUrls`: the page's presign query is NOT in its loading gate,
  //    so the first render always seeds `{}` and the URLs arrive one render
  //    later. The mount fetch used to paper over that by fetching them again;
  //    without a listener every tile would show the grey placeholder for good.
  const eventSeedRef = useRef<EventRowDTO[]>(initialEvents);
  useEffect(() => {
    if (initialEvents === eventSeedRef.current) return;
    const before = JSON.stringify(eventSeedRef.current);
    eventSeedRef.current = initialEvents;
    if (JSON.stringify(initialEvents) === before) return;
    setEvents(initialEvents);
    // Re-fetch too: the seed answers the HOST's request, which may differ from
    // this provider's current one (a filter applied since mount). The counter
    // rides the signature so the refetch cannot be deduped away.
    setSeedRefreshCounter((c) => c + 1);
  }, [initialEvents]);

  const imageSeedRef = useRef<Record<string, string>>(initialEventImageUrls);
  useEffect(() => {
    if (initialEventImageUrls === imageSeedRef.current) return;
    imageSeedRef.current = initialEventImageUrls;
    // An empty map is the "not resolved yet" state, never an answer — adopting
    // it would wipe URLs this provider already has.
    if (Object.keys(initialEventImageUrls).length === 0) return;
    setEventImageUrls((prev) => ({ ...prev, ...initialEventImageUrls }));
  }, [initialEventImageUrls]);

  // Imperative refetch of the current page. The fetch lives in
  // `runFetchRef.current`, reassigned every render so it sees the latest
  // filters/sort/page/search. The navigation effect wraps it in
  // startTransition (dims the grid/table while the whole dataset changes);
  // refreshEvents() calls it bare → a SILENT post-mutation reconcile that
  // updates rows in place without flashing the whole list.
  const runFetchRef = useRef<() => Promise<void>>(async () => {});

  const refreshEvents = useCallback(() => {
    void runFetchRef.current();
  }, []);

  // Fetch event image URLs for new events
  const fetchEventImageUrls = useCallback(async (eventList: EventRowDTO[]) => {
    if (!eventList || eventList.length === 0) return {};

    try {
      const eventIds = eventList.map((event) => event.eventId!);
      const imageUrlsResponse = await getS3EventImageUrls(eventIds);

      if (!("isServerError" in imageUrlsResponse)) {
        return imageUrlsResponse;
      } else {
        console.warn(
          "Failed to fetch event image URLs:",
          imageUrlsResponse.message,
        );
        return {};
      }
    } catch (error) {
      console.warn("Error fetching event image URLs:", error);
      return {};
    }
  }, []);

  // Fetch the current page from the API. Reassigned every render so it sees
  // the latest filters/sort/page/search. Called dimming (navigation effect)
  // or silent (refreshEvents).
  runFetchRef.current = async () => {
    try {
      const filtersCopy = [...activeFilterState.filter];

      if (archived) {
        filtersCopy.push({
          key: "archived",
          operator: "=",
          value: "true",
        });
      } else {
        filtersCopy.push({
          key: "archived",
          operator: "=",
          value: "false",
        });
      }

      const eventData = await getEvents(
        // Shared with DashboardRootPage's prefetch — the mount dedupe below
        // can only skip a request that is identical to the one already on
        // screen, and two hand-maintained field strings never stayed that way.
        eventListProjection(activeViewState.fields),
        search,
        page - 1,
        pageSize,
        filtersCopy,
        sortBy,
        sortOrder,
      );

      if ("isServerError" in eventData && eventData.isServerError === true)
        throwServerError(eventData);

      const eventsArray = eventData as EventRowDTO[];
      setEvents(eventsArray);

      // Fetch image URLs for the new events
      const newImageUrls = await fetchEventImageUrls(eventsArray);
      setEventImageUrls(newImageUrls);
    } catch {
      setEvents([]);
      setEventImageUrls({});
    }
  };

  // Navigation refetch: a filter/sort/page/search/archived change replaces the
  // whole dataset, so dimming the grid/table behind the spinner (startTransition
  // → isPending) is correct here. Post-mutation reconciles use refreshEvents()
  // and stay silent.
  //
  // Deduped by REQUEST SIGNATURE, not a dep array: the page hands us the first
  // page it already fetched as `initialEvents`, so the mount request was pure
  // duplication — and it is an expensive one, because every event list is
  // followed by `getS3EventImageUrls`, which mints one R2 listing + presign per
  // event id. With the table opening 50 rows deep that was ~100 R2 round-trips
  // on the landing screen instead of 50. The signature mirrors the request in
  // `runFetchRef` exactly (see useSignatureEffect's rules).
  const requestSignature = JSON.stringify({
    fields: eventListProjection(activeViewState.fields),
    search,
    page: page - 1,
    pageSize,
    filters: stableFilters ?? null,
    archived,
    sortBy,
    sortOrder,
    // Forced-refresh counter — bumped when the host hands down a fresher seed
    // (see above). Without it in the signature that refetch would be deduped.
    seedRefreshCounter,
  });
  useSignatureEffect(requestSignature, () => {
    startTransition(async () => {
      await runFetchRef.current();
    });
  });

  // Shared helper for removing items and handling pagination
  const removeItemsAndRefresh = useCallback(
    (eventIds: string[]) => {
      const removedCount = eventIds.length;
      setTotalCount((prev) => prev - removedCount);

      setEvents((prev) => prev.filter((event) => !eventIds.includes(event.eventId!)));

      // Read the post-setEvents count from the ref so back-to-back removals don't
      // read a stale closure-captured `events` snapshot.
      const remaining = eventsStateRef.current.filter(
        (event) => !eventIds.includes(event.eventId!),
      );
      if (remaining.length === 0) {
        // On page 1 there is no previous page to fall back to, so setPage is a
        // no-op and NOTHING refetched — emptying the first page left the list
        // blank until something else happened to reload it.
        if (page > 1) setPage((p) => (p > 1 ? p - 1 : p));
        else refreshEvents();
      } else if (remaining.length < pageSize) {
        refreshEvents();
      }
    },
    [pageSize, page, setPage, refreshEvents],
  );

  // The dashboard grid + count + quota (and the subscription page's event
  // list) are react-query snapshots that outlive this provider; every write
  // below mutates only provider state + direct refetches. Mark both
  // namespaces stale so a sub-staleTime remount can't resurrect archived/
  // deleted events or show stale counts. refetchType "none": the provider's
  // own refreshEvents keeps the mounted grid live.
  const queryClient = useQueryClient();
  const invalidateEventListCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["root"], refetchType: "none" });
    void queryClient.invalidateQueries({ queryKey: ["userSubscription"], refetchType: "none" });
  }, [queryClient]);

  // Unified runner for archive / unarchive / delete. Handles server errors consistently
  // and pipes through a per-row aggregate (`BulkActionResult`) so UI callers can surface
  // partial failures.
  const runEventBulk = useCallback(
    async (
      apiCall: (s: BulkSelection) => Promise<unknown>,
      selection: BulkSelection,
    ) => {
      const response = await apiCall(selection);
      if (response && typeof response === "object" && "isServerError" in response) {
        throwServerError(response as ServerError);
      }
      const outcome = normalizeBulkOutcome(response as BulkActionResult | undefined);

      // Only tell the caches something changed if something actually did.
      if (!outcome || outcome.processed > 0) invalidateEventListCaches();

      if (selection.mode === "ids") {
        // This used to drop every selected id from local state without ever
        // reading `failed`, so a refused delete looked exactly like a
        // successful one: the row vanished, an error appeared next to it, and
        // the next refetch put it back. Rows the server says are still there
        // stay; anything we cannot classify stays too and forces a refetch,
        // because a row wrongly left behind self-corrects while a row wrongly
        // removed HIDES an event that still exists.
        const { goneIds, needsServerReconcile } = splitFailedIds(outcome, selection.ids);
        if (goneIds.size > 0) removeItemsAndRefresh([...goneIds]);
        if (needsServerReconcile) refreshEvents();
      } else {
        refreshEvents();
      }
      return response as EventBulkOutcome;
    },
    [removeItemsAndRefresh, refreshEvents, invalidateEventListCaches],
  );

  const archiveEvents = useCallback(
    async (selection: BulkSelection): Promise<EventBulkOutcome> =>
      runEventBulk(archiveEventsApi, selection),
    [runEventBulk],
  );

  const unarchiveEvents = useCallback(
    async (selection: BulkSelection): Promise<EventBulkOutcome> =>
      runEventBulk(unarchiveEventsApi, selection),
    [runEventBulk],
  );

  const deleteEvents = useCallback(
    async (selection: BulkSelection): Promise<EventBulkOutcome> =>
      runEventBulk(deleteEventsApi, selection),
    [runEventBulk],
  );

  const duplicateEvent = useCallback(async (
    eventId: string,
    name?: string,
    copyGuests?: boolean,
  ): Promise<DuplicateEventResponse> => {
    const result = await duplicateEventApi(eventId, name, copyGuests);

    if ("isServerError" in result) {
      throwServerError(result);
    }

    setTotalCount((prev) => prev + 1);
    invalidateEventListCaches();
    refreshEvents();
    return result;
  }, [refreshEvents, setTotalCount, invalidateEventListCaches]);

  const eventsRef = useRef(events);
  eventsRef.current = events;

  const buildUpdateEvents = useCallback(
    (eventId: string, field: Field, newValue: string | string[]) => {
      if (!eventId || !field) return;

      const existingEvent = eventsRef.current.find((event) => event.eventId === eventId);
      if (!existingEvent) return;

      const current = pendingUpdatesRef.current;
      let update = current.find((u) => u.eventId === eventId);
      if (!update) {
        update = { eventId } as EventRowDTO;
      } else {
        update = { ...update };
      }
      parseEventsSpecial(update, field, newValue);

      // Validate start/end date constraint
      if (field.name === "start" || field.name === "end") {
        const merged = { ...existingEvent, ...update };
        const start = merged.start ? new Date(merged.start) : undefined;
        const end = merged.end ? new Date(merged.end) : undefined;

        if (start && end && start > end) {
          setFieldErrors((prev) => ({
            ...prev,
            [eventId]: { ...prev[eventId], [field.name]: "startDateEndDate" },
          }));
        } else if (fieldErrorsRef.current[eventId]?.start || fieldErrorsRef.current[eventId]?.end) {
          setFieldErrors((prev) => {
            const { start: _s, end: _e, ...rest } = prev[eventId] || {};
            if (Object.keys(rest).length === 0) {
              const { [eventId]: _, ...remaining } = prev;
              return remaining;
            }
            return { ...prev, [eventId]: rest };
          });
        }
      }

      // Required-field guard: name is @NotBlank on the backend. Block the save
      // banner when the user empties a required text field rather than waiting
      // for a server-side rejection.
      if (field.name === "name") {
        const trimmed = typeof newValue === "string" ? newValue.trim() : "";
        if (trimmed === "") {
          setFieldErrors((prev) => ({
            ...prev,
            [eventId]: { ...prev[eventId], name: "eventNameRequired" },
          }));
        } else if (fieldErrorsRef.current[eventId]?.name) {
          setFieldErrors((prev) => {
            const { name: _n, ...rest } = prev[eventId] || {};
            if (Object.keys(rest).length === 0) {
              const { [eventId]: _, ...remaining } = prev;
              return remaining;
            }
            return { ...prev, [eventId]: rest };
          });
        }
      }

      const filtered = current.filter((item) => item.eventId !== eventId);
      pendingUpdatesRef.current = [...filtered, update];
      setHasPendingChanges(true);
    },
    [],
  );

  /**
   * The pending edit that would count as a big forward jump. Computed from the
   * same pending partial that is about to be PUT, against the stored row — the
   * identical judgement the server makes.
   */
  const getPendingDateJumps = useCallback((): PendingDateJump[] => {
    const jumps: PendingDateJump[] = [];
    // ALL of them: the banner saves every pending row at once, so reporting
    // only the first offender spends the second event's budget unwarned.
    for (const update of pendingUpdatesRef.current) {
      if (update.start === undefined && update.end === undefined) continue;
      const stored = eventsRef.current.find((e) => e.eventId === update.eventId);
      if (!stored) continue;
      if (isBigForwardJump({ start: update.start, end: update.end }, stored.end)) {
        jumps.push({
          eventId: update.eventId!,
          name: stored.name ?? "",
          spent: stored.bigDateJumpCount ?? 0,
        });
      }
    }
    return jumps;
  }, []);

  /**
   * Every pending edit that moves an event's dates or timezone, with the stored
   * values alongside the new ones. The server re-derives that event's scheduled
   * automations on exactly this change, so the toolbar can warn about it the
   * same way the settings form does.
   */
  const getPendingDateEdits = useCallback((): PendingDateEdit[] => {
    const edits: PendingDateEdit[] = [];
    for (const update of pendingUpdatesRef.current) {
      if (
        update.start === undefined &&
        update.end === undefined &&
        update.timeZone === undefined
      )
        continue;
      const stored = eventsRef.current.find((e) => e.eventId === update.eventId);
      if (!stored) continue;
      const asIso = (v: Date | string | undefined) => {
        const ms = toInstantMs(v);
        return ms === null ? undefined : new Date(ms).toISOString();
      };
      edits.push({
        eventId: update.eventId!,
        name: stored.name ?? "",
        plugins: (stored.plugins ?? []) as string[],
        oldStart: asIso(stored.start),
        oldEnd: asIso(stored.end),
        newStart: asIso(update.start ?? stored.start),
        newEnd: asIso(update.end ?? stored.end),
        // RAW zones: undefined is meaningful to the impact predicate.
        oldTimeZone: stored.timeZone as string | undefined,
        newTimeZone: (update.timeZone ?? stored.timeZone) as string | undefined,
      });
    }
    return edits;
  }, []);

  /**
   * Mark one event's date cells as refused, the way the local start/end check
   * already does. Used when the server would answer 409 dateJumpLimitReached:
   * the row shows why, Save stays blocked until the user reverts it, and NO
   * pending edit is thrown away — the previous behaviour discarded every row's
   * work to undo one date.
   */
  const flagDateJumpBlocked = useCallback((eventId: string) => {
    setFieldErrors((prev) => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        end: "dateJumpLimitReached",
        start: "dateJumpLimitReached",
      },
    }));
  }, []);

  const saveUpdateEvents = useCallback(async () => {
    if (Object.keys(fieldErrorsRef.current).length > 0) return;
    const updatesToSave = pendingUpdatesRef.current;
    if (updatesToSave.length === 0) return;

    // Which of these spend the one-time jump budget. The server increments it
    // and answers void, so mirror the bump locally on success — otherwise a
    // second jump on the same row in the same session shows the warning again
    // and then eats a 409, instead of steering to Duplicate.
    const spendingJumpIds = new Set(getPendingDateJumps().map((j) => j.eventId));

    pendingUpdatesRef.current = [];

    try {
      const updatesMap = new Map(
        updatesToSave.map((update) => [update.eventId, update]),
      );

      setEvents((prev) =>
        prev.map((event) => {
          const update = updatesMap.get(event.eventId);
          if (!update) return event;
          const merged = { ...event, ...update };
          if (event.eventId && spendingJumpIds.has(event.eventId)) {
            merged.bigDateJumpCount = (event.bigDateJumpCount ?? 0) + 1;
          }
          return merged;
        }),
      );

      const result = await updateEvents(updatesToSave);

      // updateEvents RETURNS a ServerError rather than throwing, so the catch
      // below won't fire on a rejection (e.g. dateJumpLimitReached when an event
      // has already used its one allowed far-future date move). Without this the
      // optimistic row update above would falsely persist on screen. Undo it by
      // reloading server truth and keep the changes pending for retry/discard.
      if (result && typeof result === "object" && "isServerError" in result) {
        pendingUpdatesRef.current = updatesToSave;
        setHasPendingChanges(true);
        // Say WHY. The revert alone reads as the app losing the edit — and
        // pass the WHOLE payload, not just the code: translateBackendError
        // appends `detail` for catch-all codes like validationError, which is
        // the difference between "Validation error" and naming the field.
        const err = result as { message?: unknown; detail?: unknown };
        setSaveError({
          message: typeof err.message === "string" ? err.message : "unknown",
          detail: typeof err.detail === "string" ? err.detail : undefined,
        });
        refreshEvents();
        return;
      }
      setSaveError(null);

      // After save + render flush, blur may have re-added the same values.
      // Discard stale pending entries that match what was just saved.
      const savedIds = new Set(updatesToSave.map((u) => u.eventId));
      pendingUpdatesRef.current = pendingUpdatesRef.current.filter(
        (p) => !savedIds.has(p.eventId)
      );
      setHasPendingChanges(pendingUpdatesRef.current.length > 0);
      invalidateEventListCaches();
    } catch (error) {
      console.error("Failed to update events:", error);
      pendingUpdatesRef.current = updatesToSave;
      setHasPendingChanges(true);
      setSaveError("unknown");
    }
  }, [refreshEvents, invalidateEventListCaches, getPendingDateJumps]);

  const discardPendingUpdates = useCallback(() => {
    pendingUpdatesRef.current = [];
    setHasPendingChanges(false);
    setFieldErrors({});
  }, []);

  const contextValue = useMemo(() => ({
    saveUpdateEvents,
    duplicateEvent,
    archiveEvents,
    unarchiveEvents,
    deleteEvents,
    buildUpdateEvents,
    discardPendingUpdates,
    hasPendingChanges,
    saveError,
    dismissSaveError,
    getPendingDateJumps,
    getPendingDateEdits,
    flagDateJumpBlocked,
    fieldErrors,
    events,
    eventImageUrls,
    loading: isPending,
  }), [saveUpdateEvents, duplicateEvent, archiveEvents, unarchiveEvents, deleteEvents, buildUpdateEvents, discardPendingUpdates, hasPendingChanges, saveError, dismissSaveError, getPendingDateJumps, getPendingDateEdits, flagDateJumpBlocked, fieldErrors, events, eventImageUrls, isPending]);

  return (
    <EventsContext.Provider value={contextValue}>
      {children}
    </EventsContext.Provider>
  );
};

export const useEvents = () => {
  const context = useContext(EventsContext);
  if (!context)
    throw new Error("useEvents must be used within an EventsContext");
  return context;
};
